/**
 * useInvoiceFormEffects
 *
 * All useEffect hooks extracted from the invoice form.
 * Receives state setters from useInvoiceForm and runs side-effects.
 *
 * Heavy fetcher / normalization logic lives in
 * ./useInvoiceFormEffects-helpers.ts to keep individual effects small.
 */

import { useEffect, useRef } from 'react'

import type { Address } from '@/gen/proto'

import {
  lookupCountryCode,
  lookupCountryName,
  creditDaysToSelectValue,
  creditDaysToTermString,
} from './invoice-form-helpers'
import {
  fetchBankAccountsRaw,
  pickInitialBankSelection,
  fetchAndNormalizeEntityAddresses,
  fetchLastInvoiceForParty,
  applyLastInvoiceAddresses,
  autoFillExportAddressesFromParty,
  autoFillExportShipToFromParty,
  loadPartyRoleOptions,
} from './useInvoiceFormEffects-helpers'
import { getExchangeRate } from '../../../services/currency-service'
import { searchCustomerPOsForInvoicing } from '../../../services/customer-purchase-order-service'
import { getPartyOutstanding } from '../../../services/reporting-aging-service'
import { formatAmount } from '../../../utils/billing-helpers'

import type { InvoiceFormProps } from './types'
import type { UseInvoiceFormReturn } from './useInvoiceForm'

interface EffectsParams {
  form: UseInvoiceFormReturn
  initialData: InvoiceFormProps['initialData']
  sellerDetails: InvoiceFormProps['sellerDetails']
  isVendor: boolean
}

export function useInvoiceFormEffects(params: EffectsParams): void {
  const { form, initialData, sellerDetails, isVendor } = params

  useShipToFromReconstructedAddress(form, initialData)
  useSyncFormFromInitialData(form, initialData)
  useBankAccountsLoader(form, initialData, sellerDetails)
  useExchangeRateAutoFetch(form, initialData)
  useCustomerPOsLoader(form, sellerDetails)
  useEntityAddressesLoader(form)
  useAutoDueDate(form)
  useAutoFillCurrencyFromParty(form, initialData)
  useAutoCountryFromPartyAddress(form, initialData)
  useAutoFillPaymentTermsFromParty(form, initialData)
  usePartyOutstandingLoader(form, sellerDetails, isVendor)
  useVendorCodeCapture(form)
  useResetDerivedAddressesOnPartyChange(form)
  useLastInvoiceAddressAutoFill(form, sellerDetails, initialData)
  useAutoSelectBillingForB2BB2C(form, initialData)
  useAutoFillCountryFromSelectedAddress(form)
  useRecalculateLineItemsOnSupplyType(form)
  useZeroTaxOnUrpPurchase(form)
  useCreditLimitWarning(form, isVendor)
  usePartyRoleOptionsLoader(form, sellerDetails, isVendor, initialData)
}

// ─────────────────────────────────────────────────────────────────────────────
// Individual effects
// ─────────────────────────────────────────────────────────────────────────────

function useShipToFromReconstructedAddress(
  form: UseInvoiceFormReturn,
  initialData: InvoiceFormProps['initialData'],
): void {
  const { reconstructedShipToAddress, setSelectedConsigneeAddress, setSelectedShipToAddress } = form
  useEffect(() => {
    if (!initialData || !reconstructedShipToAddress) { return }
    const t = initialData.invoiceType
    if (t === 'b2b' || t === 'b2c' || t === 'vendor_bill') {
      setSelectedConsigneeAddress(reconstructedShipToAddress)
    } else if (t === 'export') {
      setSelectedShipToAddress(reconstructedShipToAddress)
    }
  }, [initialData, reconstructedShipToAddress]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useSyncFormFromInitialData(
  form: UseInvoiceFormReturn,
  initialData: InvoiceFormProps['initialData'],
): void {
  const { setInvoiceType, setExportType, setSelectedShipToAddress, setSelectedConsigneeAddress, setSelectedNotifyPartyAddress, setPartyRole } = form
  useEffect(() => {
    if (!initialData) { return }
    if (initialData.invoiceType) { setInvoiceType(initialData.invoiceType) }
    if (initialData.exportType) { setExportType(initialData.exportType) }
    if (initialData.shipToAddress) { setSelectedShipToAddress(initialData.shipToAddress as Address) }
    if (initialData.consigneeAddress) { setSelectedConsigneeAddress(initialData.consigneeAddress as Address) }
    if (initialData.notifyPartyAddress) { setSelectedNotifyPartyAddress(initialData.notifyPartyAddress as Address) }
    if (initialData.partyRole) { setPartyRole(initialData.partyRole) }
  }, [initialData]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useBankAccountsLoader(
  form: UseInvoiceFormReturn,
  initialData: InvoiceFormProps['initialData'],
  sellerDetails: InvoiceFormProps['sellerDetails'],
): void {
  const {
    setBankAccounts, setBankGLAccounts, setCashGLAccounts,
    setSelectedBankAccountId, selectedBankAccountId,
  } = form
  useEffect(() => {
    async function load(): Promise<void> {
      if (!sellerDetails.orgId || !sellerDetails.ledgerId) { return }
      try {
        const { bankOnly, glBankAccounts, cashGLAccounts } = await fetchBankAccountsRaw(
          sellerDetails.orgId, sellerDetails.ledgerId,
        )
        setBankAccounts(bankOnly)
        setBankGLAccounts(glBankAccounts)
        setCashGLAccounts(cashGLAccounts)

        const selection = pickInitialBankSelection({
          bankOnly, glBankAccounts,
          initialBankDetails: initialData?.bankDetails,
          selectedBankAccountId,
        })
        if (selection) { setSelectedBankAccountId(selection) }
      } catch { /* proceed without */ }
    }
    void load()
  }, [sellerDetails.orgId, sellerDetails.ledgerId]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useExchangeRateAutoFetch(
  form: UseInvoiceFormReturn,
  initialData: InvoiceFormProps['initialData'],
): void {
  const { currency, baseCurrency, currencyChangedByUser, setExchangeRate, setErrors, setIsFetchingRate } = form
  useEffect(() => {
    if (!currencyChangedByUser.current && initialData?.id && initialData?.exchangeRate) { return }

    async function fetchRate(): Promise<void> {
      if (currency === baseCurrency) { setExchangeRate('1.0'); return }
      setIsFetchingRate(true)
      try {
        const result = await getExchangeRate(currency, baseCurrency)
        if (result.data?.rate) {
          setExchangeRate(result.data.rate.toFixed(2))
          setErrors((prev) => { const next = { ...prev }; delete next.exchangeRate; return next })
        } else {
          setErrors((prev) => ({
            ...prev,
            exchangeRate: `Could not fetch rate for ${currency}/${baseCurrency}. Please enter manually.`,
          }))
        }
      } catch {
        setErrors((prev) => ({ ...prev, exchangeRate: 'Failed to fetch exchange rate. Please enter manually.' }))
      } finally {
        setIsFetchingRate(false)
      }
    }
    void fetchRate()
  }, [currency, baseCurrency]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useCustomerPOsLoader(
  form: UseInvoiceFormReturn,
  sellerDetails: InvoiceFormProps['sellerDetails'],
): void {
  const { selectedParty, invoiceType, setCustomerPOs } = form
  useEffect(() => {
    async function load(): Promise<void> {
      const { orgId, ledgerId } = sellerDetails
      if (!orgId || !ledgerId || !selectedParty
        || invoiceType === 'credit_note' || invoiceType === 'debit_note') {
        setCustomerPOs([]); return
      }
      try {
        const response = await searchCustomerPOsForInvoicing(orgId, ledgerId, selectedParty.id)
        setCustomerPOs(response.data?.customerPurchaseOrders ?? [])
      } catch { setCustomerPOs([]) }
    }
    void load()
  }, [sellerDetails.orgId, sellerDetails.ledgerId, selectedParty, invoiceType]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useEntityAddressesLoader(form: UseInvoiceFormReturn): void {
  const { selectedParty, setEntityAddresses } = form
  useEffect(() => {
    async function load(): Promise<void> {
      if (!selectedParty?.entityId) { setEntityAddresses([]); return }
      try {
        const addresses = await fetchAndNormalizeEntityAddresses(selectedParty.entityId)
        setEntityAddresses(addresses)
      } catch { setEntityAddresses([]) }
    }
    void load()
  }, [selectedParty]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useAutoDueDate(form: UseInvoiceFormReturn): void {
  const { invoiceDate, selectedPaymentTermValue, customCreditDays, setDueDate } = form
  useEffect(() => {
    if (!invoiceDate) { return }
    const days = selectedPaymentTermValue === 'custom'
      ? (parseInt(customCreditDays, 10) || 0)
      : (parseInt(selectedPaymentTermValue, 10) || 0)
    const newDueDate = new Date(invoiceDate)
    newDueDate.setDate(newDueDate.getDate() + days)
    setDueDate(newDueDate)
  }, [invoiceDate, selectedPaymentTermValue, customCreditDays]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useAutoFillCurrencyFromParty(
  form: UseInvoiceFormReturn,
  initialData: InvoiceFormProps['initialData'],
): void {
  const { selectedParty, setCurrency } = form
  useEffect(() => {
    const c = selectedParty?.defaultCurrency?.trim()
    if (c && !initialData) {
      setCurrency(c)
    }
  }, [selectedParty?.defaultCurrency, initialData]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useAutoCountryFromPartyAddress(
  form: UseInvoiceFormReturn,
  initialData: InvoiceFormProps['initialData'],
): void {
  const { selectedParty, invoiceType, setCountryOfDestination, setCountryOfDestinationCode } = form
  useEffect(() => {
    if (!selectedParty || initialData !== undefined || invoiceType !== 'export') { return }
    const addr = selectedParty.billingAddress ?? selectedParty.shippingAddress
    if (!addr) { return }
    const code = addr.countryCode?.toUpperCase()
      ?? lookupCountryCode(addr.country ?? '')?.toUpperCase()
    if (code) {
      setCountryOfDestinationCode(code)
      setCountryOfDestination(addr.country ?? lookupCountryName(code))
    }
  }, [selectedParty, initialData, invoiceType]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useAutoFillPaymentTermsFromParty(
  form: UseInvoiceFormReturn,
  initialData: InvoiceFormProps['initialData'],
): void {
  const { selectedParty, setSelectedPaymentTermValue, setCustomCreditDays, setPaymentTerms } = form
  useEffect(() => {
    if (selectedParty?.creditDays === undefined || initialData) { return }
    const days = selectedParty.creditDays
    const selectVal = creditDaysToSelectValue(days)
    setSelectedPaymentTermValue(selectVal)
    if (selectVal === 'custom') { setCustomCreditDays(String(days)) }
    setPaymentTerms(creditDaysToTermString(days))
  }, [selectedParty?.creditDays, initialData]) // eslint-disable-line react-hooks/exhaustive-deps
}

function usePartyOutstandingLoader(
  form: UseInvoiceFormReturn,
  sellerDetails: InvoiceFormProps['sellerDetails'],
  isVendor: boolean,
): void {
  const { selectedParty, setPartyOutstanding, setCreditLimitWarning } = form
  useEffect(() => {
    async function load(): Promise<void> {
      if (isVendor || !selectedParty?.id || !sellerDetails.orgId || !selectedParty.creditLimit) {
        setPartyOutstanding(0)
        setCreditLimitWarning(null)
        return
      }
      try {
        const result = await getPartyOutstanding(sellerDetails.orgId, selectedParty.id)
        if (result.data) { setPartyOutstanding(result.data.totalOutstanding) }
      } catch { setPartyOutstanding(0) }
    }
    void load()
  }, [isVendor, selectedParty?.id, sellerDetails.orgId, selectedParty?.creditLimit]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useVendorCodeCapture(form: UseInvoiceFormReturn): void {
  const { selectedParty, setPartyVendorCode } = form
  useEffect(() => {
    setPartyVendorCode(selectedParty?.companyVendorCode ?? '')
  }, [selectedParty?.companyVendorCode]) // eslint-disable-line react-hooks/exhaustive-deps
}

// When the selected party changes, clear every party-derived address so the new
// party never inherits the previous one's ship-to / consignee / notify-party
// address. The auto-fill effects below then repopulate fresh addresses.
// Skipped on the very first selection (previousPartyId === null) so that the
// initial saved-invoice addresses are preserved in edit mode on first load.
function useResetDerivedAddressesOnPartyChange(form: UseInvoiceFormReturn): void {
  const {
    selectedParty,
    setSelectedShipToAddress, setSelectedConsigneeAddress, setSelectedNotifyPartyAddress,
    setShipToType, setConsigneeType, setNotifyPartyType,
  } = form
  const prevPartyId = useRef<string | null>(null)
  const partyId = selectedParty?.id ?? null
  useEffect(() => {
    if (prevPartyId.current === partyId) { return }
    const previousPartyId = prevPartyId.current
    prevPartyId.current = partyId
    // First selection (new invoice) or first load (edit mode): nothing to clear.
    if (previousPartyId === null) { return }
    setSelectedShipToAddress(null)
    setSelectedConsigneeAddress(null)
    setSelectedNotifyPartyAddress(null)
    setShipToType('same')
    setConsigneeType('same')
    setNotifyPartyType('none')
  }, [partyId]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useLastInvoiceAddressAutoFill(
  form: UseInvoiceFormReturn,
  sellerDetails: InvoiceFormProps['sellerDetails'],
  initialData: InvoiceFormProps['initialData'],
): void {
  const {
    selectedParty, invoiceType, allPartyAddresses,
    setSelectedShipToAddress, setSelectedConsigneeAddress, setShipToType, setConsigneeType,
  } = form
  // In edit mode, skip the auto-fill on the FIRST party (addresses come from
  // the saved invoice). After a user changes the party, allow auto-fill normally.
  const isInitialEditParty = useRef(Boolean(initialData))
  useEffect(() => {
    async function run(): Promise<void> {
      if (!selectedParty || !sellerDetails?.orgId || !sellerDetails?.ledgerId) { return }
      if (isInitialEditParty.current) { isInitialEditParty.current = false; return }
      const setters = {
        allPartyAddresses, setSelectedShipToAddress, setSelectedConsigneeAddress,
        setShipToType, setConsigneeType,
      }
      try {
        const lastInvoice = await fetchLastInvoiceForParty({
          orgId: sellerDetails.orgId, ledgerId: sellerDetails.ledgerId, partyId: selectedParty.id,
        })
        if (lastInvoice) {
          applyLastInvoiceAddresses({ lastInvoice, invoiceType, ...setters })
          return
        }
        if (invoiceType === 'export') {
          autoFillExportAddressesFromParty(setters)
        }
      } catch {
        if (invoiceType === 'export') { autoFillExportShipToFromParty(setters) }
      }
    }
    void run()
  }, [selectedParty, sellerDetails?.orgId, sellerDetails?.ledgerId, initialData, invoiceType, allPartyAddresses]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useAutoSelectBillingForB2BB2C(
  form: UseInvoiceFormReturn,
  initialData: InvoiceFormProps['initialData'],
): void {
  const { selectedParty, invoiceType, setSelectedShipToAddress, setShipToType } = form
  const autoSelectedBillingForParty = useRef<string | null>(null)
  useEffect(() => {
    const shouldAutoSelect = (invoiceType === 'b2b' || invoiceType === 'b2c')
      && selectedParty && !initialData && selectedParty.billingAddress
      && autoSelectedBillingForParty.current !== selectedParty.id
    if (!shouldAutoSelect) { return }
    autoSelectedBillingForParty.current = selectedParty.id
    setSelectedShipToAddress(selectedParty.billingAddress as Address)
    setShipToType('same')
  }, [selectedParty, invoiceType, initialData]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useAutoFillCountryFromSelectedAddress(form: UseInvoiceFormReturn): void {
  const {
    invoiceType, exportType, selectedConsigneeAddress, selectedShipToAddress,
    setCountryOfDestination, setCountryOfDestinationCode,
  } = form
  useEffect(() => {
    if (invoiceType !== 'export') { return }
    const sourceAddress = exportType === 'services' ? selectedConsigneeAddress : selectedShipToAddress
    if (sourceAddress?.country && sourceAddress?.countryCode) {
      setCountryOfDestination(sourceAddress.country)
      setCountryOfDestinationCode(sourceAddress.countryCode)
    }
  }, [invoiceType, exportType, selectedConsigneeAddress, selectedShipToAddress]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useRecalculateLineItemsOnSupplyType(form: UseInvoiceFormReturn): void {
  const { calculateLineItem, setLineItems } = form
  useEffect(() => {
    setLineItems((items) => items.map(calculateLineItem))
  }, [form.supplyType, calculateLineItem]) // eslint-disable-line react-hooks/exhaustive-deps
}

// An unregistered supplier (URP) charges no tax, so URP purchases must always
// carry 0% GST. This effect enforces that:
//   - On load OR on entry into URP → zero the GST on every line (wasUrp starts
//     false, so a URP bill opened for edit is zeroed on first run too — this also
//     repairs bills wrongly saved with tax).
//   - On transition out of URP (party changed to a registered one) → restore 18%.
// wasUrp starts at false (not null), so a registered-party bill on first load
// hits neither branch and keeps its saved rate (e.g. a genuine 12% bill).
function useZeroTaxOnUrpPurchase(form: UseInvoiceFormReturn): void {
  const { isUrpPurchase, calculateLineItem, setLineItems } = form
  const wasUrp = useRef(false)
  useEffect(() => {
    if (isUrpPurchase && !wasUrp.current) {
      setLineItems((items) => items.map((it) => calculateLineItem({ ...it, gstRate: '0' })))
    } else if (!isUrpPurchase && wasUrp.current) {
      setLineItems((items) => items.map((it) => calculateLineItem({ ...it, gstRate: '18' })))
    }
    wasUrp.current = isUrpPurchase
  }, [isUrpPurchase, calculateLineItem, setLineItems])
}

// Loads the active party-role options for this document's direction and
// pre-fills form.partyRole with the org's default (or the built-in
// customer/supplier role) — but only when the invoice doesn't already carry
// an explicit role, so a saved invoice's stored role (or a role the user has
// already picked) is never clobbered.
function usePartyRoleOptionsLoader(
  form: UseInvoiceFormReturn,
  sellerDetails: InvoiceFormProps['sellerDetails'],
  isVendor: boolean,
  initialData: InvoiceFormProps['initialData'],
): void {
  const { partyRole, setPartyRole, setRoleOptions } = form
  useEffect(() => {
    async function load(): Promise<void> {
      if (!sellerDetails.orgId) { setRoleOptions([]); return }
      try {
        const { options, defaultRole } = await loadPartyRoleOptions(sellerDetails.orgId, isVendor)
        setRoleOptions(options)
        if (!initialData?.partyRole && !partyRole && defaultRole) { setPartyRole(defaultRole) }
      } catch { setRoleOptions([]) }
    }
    void load()
  }, [sellerDetails.orgId, isVendor]) // eslint-disable-line react-hooks/exhaustive-deps
}

function useCreditLimitWarning(form: UseInvoiceFormReturn, isVendor: boolean): void {
  const { selectedParty, partyOutstanding, totals, currency, setCreditLimitWarning } = form
  useEffect(() => {
    if (isVendor || !selectedParty?.creditLimit) {
      setCreditLimitWarning(null)
      return
    }
    const creditLimit = Number(selectedParty.creditLimit)
    const projectedOutstanding = partyOutstanding + totals.grandTotal
    if (projectedOutstanding > creditLimit) {
      const limitStr = formatAmount(creditLimit, currency)
      const outstandingStr = formatAmount(partyOutstanding, currency)
      const projectedStr = formatAmount(projectedOutstanding, currency)
      setCreditLimitWarning(
        `This invoice will bring ${selectedParty.name}'s total outstanding to ${projectedStr} against a credit limit of ${limitStr}. Current outstanding: ${outstandingStr}.`,
      )
    } else {
      setCreditLimitWarning(null)
    }
  }, [isVendor, selectedParty?.creditLimit, selectedParty?.name, partyOutstanding, totals.grandTotal, currency]) // eslint-disable-line react-hooks/exhaustive-deps
}
